require('./RAContributions.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const moment = require('moment');
const classnames = require('classnames');

// @commons
const { OKLink } = require('commonsComponents/OKLink');
const { OKButton } = require('components/commons/OKButton/OKButton');
const { LoadingSection } = require('commonsComponents/loadingSection');

// @constants
const { EURO_SYMBOL } = require('constants/index');
const {
    MINIMUM_PERIODIC_CONTRIBUTION,
    CONTRIBUTION_PERIOD_DAILY,
    CONTRIBUTION_PERIOD_WEEKLY,
    CONTRIBUTION_PERIOD_MONTHLY,
    DATE_FORMAT,
    RA_DISSOCIATE_OBJECTIVE_URL,
} = require('../../../raConstants/index');

// @utilities
const { formatText, FormattedText } = require('core/i18n').i18n;
const DateHelper = require('utilities/dateHelper');
const { formatAmount } = require('utilities/currencyHelper');

class RAContributions extends React.Component {
    getPeriodicityLabel(periodicityTransferCode) {
        switch (Number(periodicityTransferCode)) {
            case CONTRIBUTION_PERIOD_DAILY:
                return formatText('raContributionPeriodicity-daily');
            case CONTRIBUTION_PERIOD_WEEKLY:
                return formatText('raContributionPeriodicity-weekly');
            case CONTRIBUTION_PERIOD_MONTHLY:
                return formatText('raContributionPeriodicity-monthly');
            case MINIMUM_PERIODIC_CONTRIBUTION:
                return formatText('raContributionPeriodicity-yearly');
            default:
                return '';
        }
    }

    getContributionContent() {
        const {
            managementLink,
            periodicTransferAmount,
            periodicTransferDate,
            periodicityTransferCode,
            isCancelInProgress,
            contributionsManageLinkClick,
        } = this.props;
        const formattedTransferAmount = `${formatAmount(
            periodicTransferAmount,
        )} ${EURO_SYMBOL}`;
        const formattedTransferDate = !periodicTransferDate
            ? formatText('broker-notAvailableData')
            : DateHelper.buildFormattedDate(
                  moment(periodicTransferDate),
                  DATE_FORMAT,
              );
        const periodicityLabel = this.getPeriodicityLabel(
            periodicityTransferCode,
        ).toLowerCase();
        let managementButton;
        const periodicityParagraph = periodicTransferDate ? (
            <FormattedText
                className="ra-contributions__declaration-para"
                value="raObjectiveTracking-contributionsDeclarationParagraph2"
                injectedStrings={[formattedTransferDate]}
            />
        ) : null;

        if (!isCancelInProgress) {
            managementButton = (
                <OKLink
                    to={managementLink}
                    inverse={true}
                    extraClass="ra-contributions__manage-link"
                    modifier="complementary"
                    onClick={contributionsManageLinkClick}
                >
                    <FormattedText value="raObjectiveTracking-contributionsManageLink" />
                </OKLink>
            );
        }

        return (
            <div>
                <FormattedText
                    className="ra-contributions__declaration-para"
                    value="raObjectiveTracking-contributionsDeclarationParagraph"
                    injectedStrings={[
                        formattedTransferAmount,
                        periodicityLabel,
                    ]}
                />
                {periodicityParagraph}
                {managementButton}
            </div>
        );
    }

    render() {
        const {
            isGoalNotReached,
            isLoading,
            periodicityTransferCode,
            isCancelInProgress,
            additionalContributionClick,
            isGoalReachedOnDate,
            portfolioId,
        } = this.props;

        const makeContributionsClasses = classnames(
            'ra-contributions__make-contributions',
            {
                'ra-contributions__make-contributions--with-no-contributions': !periodicityTransferCode,
            },
        );

        const contributionContent =
            !!periodicityTransferCode && this.getContributionContent();
        const contributionIcon = (
            <i className="pull-left ra-contributions__icon icon-incremento-moderado" />
        );
        let content;
        if (isLoading) {
            content = <LoadingSection />;
        } else {
            if (isGoalNotReached) {
                const dissociateURL = `${RA_DISSOCIATE_OBJECTIVE_URL}/${portfolioId}`;
                content = (
                    <main className="ra-contributions__main-section">
                        <FormattedText value="raObjectiveTracking-raObjectiveTracking_contributionsNotReached" />
                        <OKLink
                            to={dissociateURL}
                            inverse={true}
                            extraClass="ra-contributions__manage-link"
                            modifier="complementary"
                        >
                            <FormattedText value="raObjectiveTracking-raObjectiveTracking_contributionsDissociateLink" />
                        </OKLink>
                    </main>
                );
            } else {
                if (isGoalReachedOnDate) {
                    content = (
                        <main className="ra-contributions__main-section">
                            <FormattedText value="raObjectiveTracking-raObjectiveTracking_contributionsNoMore" />
                        </main>
                    );
                } else {
                    content = (
                        <main className="ra-contributions__main-section">
                            {contributionContent}
                            <FormattedText
                                className={makeContributionsClasses}
                                value="raObjectiveTracking-makeContributions"
                            />
                            <OKButton
                                onClick={additionalContributionClick}
                                modifier="primary"
                                disabled={isCancelInProgress}
                            >
                                <FormattedText value="raObjectiveTracking-contributionButton" />
                            </OKButton>
                        </main>
                    );
                }
            }
        }
        return (
            <section className="ra-contributions">
                <header className="clearfix">
                    {contributionIcon}
                    <FormattedText
                        className="pull-left ra-contributions__title"
                        value="raObjectiveTracking-contributionsTitle"
                    />
                </header>
                {content}
            </section>
        );
    }
}

RAContributions.propTypes = {
    isGoalNotReached: PropTypes.bool,
    isGoalReachedOnDate: PropTypes.bool,
    isLoading: PropTypes.bool,
    managementLink: PropTypes.string,
    isCancelInProgress: PropTypes.bool,
    periodicTransferAmount: PropTypes.number.isRequired,
    periodicTransferDate: PropTypes.string.isRequired,
    periodicityTransferCode: PropTypes.string.isRequired,
    portfolioId: PropTypes.string.isRequired,
    additionalContributionClick: PropTypes.func.isRequired,
    contributionsManageLinkClick: PropTypes.func.isRequired,
};

RAContributions.defaultProps = {
    isGoalNotReached: false,
    isLoading: false,
    isCancelInProgress: false,
};

module.exports = RAContributions;
